import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
  GoLink,
  SectionHeader,
  CollapsibleSection,
  TabbedContent,
  LocationCard,
  MenuSection,
  ScheduleItem,
  Steps,
  ResourceLink
} from '../specific/MessageComponent';

import {
  detectContentType,
  extractGoLinks,
  parseMenuData,
  parseLocationData,
  parseScheduleData,
  parseStepsData,
  parseResourcesData,
  parseGeneralContent, parseEventData
} from '../../utils/messageFormatUtils';

/**
 * Formats message content based on the detected type
 * @param {Object} props
 * @param {string} props.content - The message content to format
 * @returns {JSX.Element|null} The formatted message component
 */
const FormatMessageContent = ({ content }) => {
  if (!content) return null;

  // Check if the content contains markdown code blocks
  const hasCodeBlock = content.includes('```');

  // If it has code blocks, use ReactMarkdown for rendering
  if (hasCodeBlock) {
    return (
      <ReactMarkdown
        components={{
          code({node, inline, className, children, ...props}) {
            return inline ? (
              <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                {children}
              </code>
            ) : (
              <div className="bg-gray-100 dark:bg-gray-800 rounded-md p-2 my-2 overflow-x-auto">
                <code className="text-sm" {...props}>
                  {children}
                </code>
              </div>
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }

  // Detect content type
  const contentType = detectContentType(content);
  const goLinks = extractGoLinks(content);

  // For different content types, use corresponding components
  switch (contentType) {
    case 'menu': {
      const menuData = parseMenuData(content);
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">{menuData.diningHall} Dining Hall</h2>
            <div className="text-sm text-gray-600 dark:text-gray-400">{today}</div>
          </div>

          <div className="space-y-3">
            {menuData.meals.breakfast.length > 0 && (
              <MenuSection title="Breakfast" items={menuData.meals.breakfast} mealType="breakfast" />
            )}
            {menuData.meals.lunch.length > 0 && (
              <MenuSection title="Lunch" items={menuData.meals.lunch} mealType="lunch" />
            )}
            {menuData.meals.dinner.length > 0 && (
              <MenuSection title="Dinner" items={menuData.meals.dinner} mealType="dinner" />
            )}
          </div>

          {goLinks.length > 0 && (
            <div className="pt-3 mt-3 border-t dark:border-gray-700 flex flex-wrap gap-2">
              {goLinks.map((link, index) => (
                <GoLink key={index} name={link.name} />
              ))}
            </div>
          )}
        </div>
      );
    }

    default: {
      // For general content, create a nice formatted display
      const sections = parseGeneralContent(content);

      return (
        <div className="space-y-4">
          {sections.map((section, sIndex) => (
            <div key={sIndex} className={sIndex > 0 ? "mt-4" : ""}>
              {section.title && section.title !== 'Introduction' && (
                <SectionHeader title={section.title} />
              )}

              <div className="space-y-2">
                {section.items.map((item, iIndex) => {
                  // Check if item is an object with content property
                  const itemContent = typeof item === 'object' && item !== null ? item.content : item;

                  // Filter out "For future reference" line for special handling
                  if (typeof itemContent === 'string' && itemContent.includes("For future reference")) {
                    return null; // We'll handle this separately below
                  }

                  // Handle different item types
                  if (typeof itemContent === 'string') {
                    if (itemContent.startsWith('•') || itemContent.startsWith('-')) {
                      // Bullet points
                      return (
                        <div key={iIndex} className="flex items-start space-x-2">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span className="text-gray-800 dark:text-gray-200">{itemContent.substring(1).trim()}</span>
                        </div>
                      );
                    } else if (itemContent.match(/^\d+\./)) {
                      // Numbered items
                      const number = itemContent.match(/^\d+/)[0];
                      const content = itemContent.replace(/^\d+\./, '').trim();

                      return (
                        <div key={iIndex} className="flex items-start space-x-2">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-400 text-sm font-medium">
                            {number}
                          </span>
                          <span className="text-gray-800 dark:text-gray-200 pt-1">{content}</span>
                        </div>
                      );
                    }
                  }

                  // Handle object-based items
                  if (typeof item === 'object' && item !== null) {
                    if (item.type === 'list-item') {
                      return (
                        <div key={iIndex} className="flex items-start space-x-2">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span className="text-gray-800 dark:text-gray-200">{item.content}</span>
                        </div>
                      );
                    } else if (item.type === 'paragraph' || item.type === 'text') {
                      return (
                        <div key={iIndex} className="text-gray-700 dark:text-gray-300">
                          {item.content}
                        </div>
                      );
                    }
                  }

                  // Regular text (string or unexpected format)
                  return (
                    <div key={iIndex} className="text-gray-700 dark:text-gray-300">
                      {typeof itemContent === 'string' ? itemContent : JSON.stringify(item)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Special handling for "For future reference" line */}
          {content.includes("For future reference") && (
            <div className="pt-3 mt-3 border-t dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400 italic">
                {content.split('\n').find(line => line.includes("For future reference"))}
              </div>
            </div>
          )}

          {goLinks.length > 0 && (
            <div className="pt-3 mt-3 border-t dark:border-gray-700 flex flex-wrap gap-2">
              {goLinks.map((link, index) => (
                <GoLink key={index} name={link.name} />
              ))}
            </div>
          )}
        </div>
      );
    }
  }
};

export default FormatMessageContent;